import { Camera } from "../render/Camera";
import type { Demo, Scene } from "./types";
import { makeWorld, box, ball, rng } from "./helpers";

/**
 * A Plinko / Galton board: balls rain through a quincunx of pegs, bouncing left
 * or right at each row, and pile up in the bins below into the classic bell
 * curve. A mesmerizing stress test of circle–circle collision, restitution, and
 * the sweep-and-prune broadphase keeping hundreds of contacts cheap.
 */
export const plinko: Demo = {
	id: "plinko",
	title: "Plinko board",
	blurb:
		"Balls cascade through a peg grid, kicking left or right at every row, and stack into the bins below as a live bell curve. A hypnotic showcase of many-body circle collisions and the broadphase.",
	category: "Playground & Toys",
	build(): Scene {
		const world = makeWorld();

		const cols = 9;
		const colSpacing = 1.25;
		const rowSpacing = 1.0;
		const pegRows = 8;
		const topY = -5.5;
		const halfW = ((cols - 1) / 2) * colSpacing + 0.6;
		const pegR = 0.16;

		// Quincunx peg grid: odd rows shifted half a column, kept centered.
		for (let r = 0; r < pegRows; r++) {
			const y = topY + r * rowSpacing;
			const offset = (r % 2) * 0.5 * colSpacing - 0.25 * colSpacing;
			for (let k = 0; k < cols; k++) {
				const x = (k - (cols - 1) / 2) * colSpacing + offset;
				if (Math.abs(x) > halfW - 0.3) continue;
				ball(world, x, y, pegR, { isStatic: true });
			}
		}

		// Collecting bins beneath the pegs. The floor is deliberately thick so a
		// fast ball can't punch through it on a hard, near-vertical impact.
		const binTop = topY + pegRows * rowSpacing + 0.2;
		const floorThickness = 2.5;
		const floorY = binTop + 3.4 + floorThickness / 2;
		box(world, 0, floorY, halfW * 2 + 2, floorThickness, { isStatic: true, friction: 0.7 });
		box(world, -halfW - 0.5, (binTop + floorY) / 2, 1, floorY - binTop + 2, { isStatic: true });
		box(world, halfW + 0.5, (binTop + floorY) / 2, 1, floorY - binTop + 2, { isStatic: true });
		for (let k = 0; k <= cols; k++) {
			const x = (k - cols / 2) * colSpacing;
			if (Math.abs(x) > halfW) continue;
			box(world, x, (binTop + floorY) / 2, 0.16, floorY - binTop, { isStatic: true });
		}

		const random = rng(11);
		const max = 90;
		let spawned = 0;
		let acc = 0;
		const interval = 0.12;

		// Spawn just above the first peg row with a gentle nudge: the long fall is
		// through the pegs, which keeps impact speeds bounded and the sim stable.
		const onBeforeStep = (dt: number): void => {
			if (spawned >= max) return;
			acc += dt;
			while (acc >= interval && spawned < max) {
				acc -= interval;
				ball(world, (random() - 0.5) * 0.4, topY - 0.9, 0.16, {
					restitution: 0.15,
					friction: 0.4,
					velocity: [(random() - 0.5) * 0.6, 0.4],
				});
				spawned++;
			}
		};

		return {
			world,
			camera: new Camera(0, -0.5, 27),
			onBeforeStep,
			hint: "Watch the balls bounce through the pegs and pile into a bell curve. Grab one to nudge the odds.",
		};
	},
};
